import {
  IError,
  IGizmo,
  IGizmoCallbacks,
  IGizmoEvent,
  IGizmoFeatures,
  IOnlineCatalogRequest,
  IPlatformCallbacks,
  IRootGizmoCallbacks,
  IRootGizmoData,
  ISubscription,
  TGetInfoCB,
  TGizmoType,
  TJsonData,
  TSendEventCB,
  TSetGizmoDataCB,
  TSetHandlersCB,
  TSetSubscriptionsCB,
} from '../types';
import {
  getOnlineCatalogs,
  init,
  handleEvent,
  getShowScreenGizmos,
  validate,
} from './root-gizmo-functions';
import Gizmo from './gizmo';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Dao } from '../dao';
import { filterGizmoConfigs, findGizmos } from './utils';
import { gnov } from '../utils';
import { FlowyFoundation } from '../flowy';
import SectionStateMachine from './section-state-machine';
import clone from 'clone';

interface IRootGizmoConstructor {
  /**
   * Root Gizmo's configuration
   */
  config: IGizmo;
  /**
   * Flowy id of the parent root gizmo, this is provided when the RootGizmo is being
   * edited or created by an 'annex_form'
   */
  parentRootGizmoFid?: string;
  /**
   * Callbacks use by the RootGizmo to communicate with the Foundation layer
   */
  callbacks: IRootGizmoCallbacks;
  /**
   * Flowy Foundation instance
   */
  foundation?: FlowyFoundation;
  /**
   * Root Gizmo Data that's stored on the server or locally, it will be consulted
   * and will be provided by the Foundation layer
   */
  data?: IRootGizmoData;
  /**
   * When this is provided it means we're editing a Json object
   */
  jsonData?: TJsonData;
  /**
   * Objects callback functions used to interact with functionality exposed by custom hooks
   */
  platformCallbacks?: IPlatformCallbacks;
  /**
   * Data access object instance
   */
  dao?: Dao;
  /**
   * Params used by the Platform or the Foundation layer to communicate info to
   * the RootGizmo
   */
  params: any;
}

/**
 * Structure to map uid (user defined unique ids) to fids (auto generated Flowy Ids)
 */
interface IUidToFid {
  /**
   * Key is uid and value if fid
   */
  [uid: string]: string;
}

class RootGizmo {
  /**
   * RootGizmo's configuration
   */
  private _config: IGizmo;
  /**
   * If provided it means this is a child RootGizmo
   */
  private _parentRootGizmoFid?: string;
  /**
   * Data containing all of the data of the RootGizmo (all the answers of the form)
   */
  private _data?: IRootGizmoData;
  /**
   * When this is provided it means we're editing a Json object
   */
  private _jsonData?: TJsonData;
  /**
   * Dinamically generated Gizmo features
   */
  private _features: IGizmoFeatures;
  /**
   * List of child gizmos
   */
  private _gizmos: (Gizmo | RootGizmo)[];
  /**
   * Gizmos that will be shown currently on the screen on the platform layer
   */
  private _screenGizmos: (Gizmo | RootGizmo)[];
  /**
   * Callbacks used to communicate with the Foundation layer
   */
  private _callbacks: IRootGizmoCallbacks;
  /**
   * Stream of events that will be listened by the child Gizmos
   */
  private _events: Subject<IGizmoEvent>;
  /**
   * Gizmos' subscriptions, they're used for when information of interest changes
   * a GizmoEvent is produced to notify the subscribers
   */
  private _subscriptions: ISubscription[];
  /**
   * Object which maps uids (user defined unique ids) to fids (auto generated Flowy Ids)
   */
  private _uidToFid: IUidToFid;
  /**
   * Callbacks used to interact with custom hook functionality
   */
  private _platformCallbacks?: IPlatformCallbacks;
  /**
   * Flowy Foundation instance
   */
  private _foundation?: FlowyFoundation;
  /**
   * Instance of data access object
   */
  private _dao?: Dao;
  /**
   * Parametes arriving from the platform layer
   */
  private _params: any;
  private _sectionStateMachine?: SectionStateMachine;
  /**
   * It will be set to true when root gizmo data is first created
   */
  private _rootGizmoDataCreate: boolean;

  /**
   * Online catalog requests that will be sent to the server
   */
  private _onlineCatalogRequests: IOnlineCatalogRequest[];
  /**
   * BehaviorSubject that will be used to debounce the online catalog requests
   */
  private _onlineCatalogRequestsSubject?: BehaviorSubject<any>;

  public features: Subject<IGizmoFeatures>;
  public screenGizmos: Subject<(Gizmo | RootGizmo)[]>;
  /**
   * Stream of errors that will be listened by the UI layer
   */
  public errors: Subject<IError[]>;

  constructor({
    config,
    parentRootGizmoFid,
    data,
    jsonData,
    callbacks,
    platformCallbacks,
    foundation,
    dao,
    params,
  }: IRootGizmoConstructor) {
    this._config = config;
    this._parentRootGizmoFid = parentRootGizmoFid;
    this._data = data;
    this._jsonData = jsonData;
    this._callbacks = callbacks;
    this._platformCallbacks = platformCallbacks;
    this._foundation = foundation;
    this._dao = dao;
    this._params = params;

    // Default features
    this._features = {
      label: config.label,
      display: true,
      editable: true,
      required: false,
      displayLabel: true,
      loading: false,
      processing: false,
    };

    this._gizmos = [];
    this._subscriptions = [];
    this._screenGizmos = [];
    this._events = new Subject<IGizmoEvent>();

    this._uidToFid = {};
    this._onlineCatalogRequests = [];

    this.features = new Subject<IGizmoFeatures>();
    this.screenGizmos = new Subject<(Gizmo | RootGizmo)[]>();
    this.errors = new Subject<IError[]>();

    this._rootGizmoDataCreate = false;
  }

  public init = (): Promise<RootGizmo> => {
    return new Promise<RootGizmo>(async resolve => {
      const callbacks: IGizmoCallbacks = {
        getInfoCB: this.getInfoCB,
        setGizmoDataCB: this._setGizmoDataCB,
        setHandlersCB: this._setHandlersCB,
        setSubscriptionsCB: this._setSubscriptionsCB,
        sendEventCB: this._sendEventCB,
      };

      const { sectionStateMachine } = await init({
        config: this._config,
        parentRootGizmoFid: this._parentRootGizmoFid,
        data: this._data,
        jsonData: this._jsonData,
        context: {
          callbacks,
          rootGizmoCallbacks: this._callbacks,
          platformCallbacks: this._platformCallbacks,
          foundation: this._foundation,
          rootGizmo: this,
          gizmos: this._gizmos,
          events: this._events,
          dao: this._dao,
          params: this._params,
          setData: data => {
            this._data = data;
          },
          defaultFeatures: this._features,
          setRootGizmoDataCreate: rgdc => {
            this._rootGizmoDataCreate = rgdc;
          },
        },
      });

      this._sectionStateMachine = sectionStateMachine;

      if (!this._rootGizmoDataCreate && this._params['m'] === 's') {
        // If we're not creating and the mode is 's' (show) we need to set the get the list of
        // gizmos that will be shown on the screen.
        await this._sectionStateMachine?.last();

        const renderGizmos = await getShowScreenGizmos({
          gizmo: this,
          stateMachine: this._sectionStateMachine,
        })
          ?.filter(g => g.getConfig().type !== 'section')
          .filter(g => {
            const fid = g.getConfig().uid || '';
            return !(
              fid === 'added_cancel_button' ||
              fid.startsWith('added_next_button_') ||
              fid.startsWith('added_back_button_') ||
              fid.startsWith('added_save_button_')
            );
          });
        const cancelButtonConfig: IGizmo = {
          fid: 'added_cancel_button',
          type: 'button',
          label: 'Regresar',
          uid: 'added_cancel_button',
          ops: {
            button: {
              operation: 'form.cancel',
            },
          },
        };
        const cancelButton = new Gizmo({
          config: cancelButtonConfig,
          callbacks,
          rootGizmoCallbacks: this._callbacks,
          events: this._events,
          dao: this._dao,
          params: this._params,
          ancestors: [],
          foundation: this._foundation,
        });
        await cancelButton.init();
        renderGizmos?.push(cancelButton);
        if (renderGizmos) {
          this.setScreenGizmos(renderGizmos);
        }
      } else {
        this.setScreenGizmos(this._gizmos);
      }

      if (!this._rootGizmoDataCreate) {
        // This code helps us initialize the data of the gizmos that have flux configured on
        // the display, editable and required properties. This will happen on edition.
        const gizmoConfigsWithFluxOptions = filterGizmoConfigs({
          gizmo: this._config,
          filter: 'flux.options',
        }).filter(g => g.type === 'select_field');

        for (const gizmoConfig of gizmoConfigsWithFluxOptions) {
          const gizmo = this.findGizmo({ fid: gizmoConfig.fid }) as Gizmo;

          if (gizmo) {
            const gizmoData = this.getInfoCB({
              type: 'data',
              fid: gizmoConfig.fid,
            });

            await gizmo.setData(gizmoData);
          }
        }
      }

      this._onlineCatalogRequestsSubject = new BehaviorSubject<any>(null);
      // We're going to debounce the online catalog requests so that we send them in batches
      this._onlineCatalogRequestsSubject
        .asObservable()
        .pipe(debounceTime(78))
        // .pipe(debounceTime(111))
        // .subscribe(async data => {
        .subscribe(async () => {
          // After the debounce time has finished we're going to send the online catalog requests
          const onlineCatalogRequests = clone(this._onlineCatalogRequests);
          getOnlineCatalogs({
            onlineCatalogRequests,
            context: {
              dao: this._dao,
              rootGizmoFid: this._config.fid,
              config: this._config,
            },
          }).then(() => {
            // After the online catalogs have been retrieved, we're going to send the events to
            // the gizmos so that they can update their catalogs
            for (const oCatReq of onlineCatalogRequests) {
              const event: IGizmoEvent = {
                type: 'gizmo.set-config',
                src: {
                  type: 'root-gizmo',
                  rootGizmoFid: this._config.fid,
                },
                dest: {
                  type: 'gizmo',
                  gizmoFid: oCatReq.gizmoFid,
                },
                data: {
                  gizmoSetConfig: {
                    type: 'update-catalog',
                  },
                },
              };

              this._events.next(event);
            }
          });

          // After the function to get the catalogs is called, we're going to clear the list
          this._onlineCatalogRequests = [];
        });

      resolve(this);
    });
  };

  /**
   * Get the root gizmo configuration
   * @returns {IGizmo} the root gizmo configuration
   */
  public getConfig = (): IGizmo => {
    return this._config;
  };

  /**
   * Return the root gizmo data object
   *
   * @returns {IRootGizmoData} root gizmo data object
   */
  public getData = (): IRootGizmoData | undefined => {
    return this._data;
  };

  /**
   * Set new status to the root gizmo data and generate an event to send it to the server
   *
   * @param {string} status new status
   * @param {boolean} save if true, the event will be sent to the server
   * @returns
   */
  public setStatus = (status: string, save: boolean = true): Promise<void> => {
    return new Promise<void>(async resolve => {
      const data = this._data;

      if (save) {
        if (data && data.status !== status) {
          data.action = status;
          const event: IGizmoEvent = {
            type: 'gizmo.operation',
            src: {
              type: 'gizmo',
            },
            data: {
              gizmoOperation: {
                operation: 'form.submit',
              },
            },
          };

          this._sendEventCB({ event });
        }
      } else {
        if (data && data.status !== status) {
          data.status = status;
        }
      }

      resolve();
    });
  };

  /**
   * Get the RootGizmo Features
   * @returns {IGizmoFeatures}
   */
  public getFeatures = (): IGizmoFeatures => {
    return this._features;
  };

  /**
   * Return list of gizmo instances, they can be RootGizmo or Gizmo
   * @returns {(RootGizmo | Gizmo)[]} list of gizmo instances
   */
  public getGizmos = (): (RootGizmo | Gizmo)[] => {
    return this._gizmos;
  };

  /**
   * Find a gizmo inside the RootGizmo children tree
   *
   * @param {string} uid uid of the gizmo we're looking for
   * @param {string} fid fid of the gizmo we're looking for
   * @returns
   */
  public findGizmo = ({
    uid,
    fid,
  }: IFindGizmo): (Gizmo | RootGizmo) | undefined => {
    const gizmos = findGizmos({
      gizmo: this,
      fid,
      uid,
    });

    if (gizmos.length > 0) {
      return gizmos[0];
    }

    return undefined;
  };

  /**
   * Find child gizmos that match the provided type, fid or uid
   *
   * @param {TGizmoType} type type of the child gizmo we're looking for
   * @param {string} fid fid of the child gizmo we're looking for
   * @param {string} uid uid of the child gizmo we're looking for
   *
   * @returns
   */
  public findGizmos = ({
    type,
    fid,
    uid,
  }: IFindGizmos): (RootGizmo | Gizmo)[] => {
    return findGizmos({ gizmo: this, type, fid, uid });
  };

  /**
   * Return the params
   * @returns {any} params
   */
  public getParams = (): any => {
    return this._params;
  };

  /**
   * Set the list of gizmos that will be currently shown on the screen of the platform layer
   * @param {(RootGizmo | Gizmo)[]} screenGizmos list of gizmos that will be shown on the
   *  screen of the platform layer
   */
  public setScreenGizmos = (screenGizmos: (RootGizmo | Gizmo)[]) => {
    this._screenGizmos = screenGizmos;
    this.screenGizmos.next(screenGizmos);
  };

  /**
   * Get the screen gizmos
   *
   * @returns {(RootGizmo | Gizmo)[]} the list of gizmos that are currently shown on the
   *  screen of the platform layer
   */
  public getScreenGizmos = (): (RootGizmo | Gizmo)[] => {
    return this._screenGizmos;
  };

  /**
   * Send an IGizmoEvent to the RootGizmo tree
   *
   * @param {IGizmoEvent} event gizmo event being sent to the RootGizmo tree
   */
  public sendEvent = (event: IGizmoEvent) => {
    this._events.next(event);
  };

  /**
   * RootGizmo validation
   *
   * @returns {IError[]} list of errors
   */
  public validate = async (): Promise<IError[]> => {
    return validate({
      context: {
        rootGizmo: this,
      },
    });
  };

  /**
   * Callback used by the child Gizmos to register the handlers on the RootGizmo
   *
   * @param {IGizmoHandlers} gizmoHandlers object containing the handler functions
   * @param {string} fid Flowy id of the child Gizmo
   * @param {string} ugid unique generated id, right now it's the same as fid, this was
   *  introduced if we need to deal with lists of Gizmos
   * @param {string} uid Unique id provided by the user
   */
  private _setHandlersCB: TSetHandlersCB = ({ fid, uid }) => {
    if (uid) {
      this._uidToFid[uid] = fid;
    }
  };

  /**
   * Callback used by the child Gizmos in order to ask to the RootGizmo for information
   *
   * @param {string} type type of data we're asking for
   * @param {string} fid in case we're asking for GizmoData we have to provide the fid
   * @returns
   */
  public getInfoCB: TGetInfoCB = ({
    type,
    fid,
    uid,
    catalogFid,
    path,
    parentCatalogFid,
    data,
  }) => {
    // TODO: uid logic
    // This will happen when we're asking for GizmoData by uid
    if (uid) {
      fid = this._uidToFid[uid];
    }

    if (type === 'data' && fid && this._data && this._data.d) {
      return this._data.d[fid];
    } else if (type === 'catalog' && catalogFid) {
      if (this._config.catalogs) {
        const catalog = this._config.catalogs.find(c => c.f_id === catalogFid);

        if (catalog) {
          return catalog;
        }
      }
    } else if (type === 'rootGizmo') {
      return this;
    } else if (type === 'rootGizmoFid') {
      return this._config.fid;
    } else if (type === 'rootGizmoConfig') {
      return this._config;
    } else if (type === 'rootGizmoData') {
      return this._data;
    } else if (type === 'rootGizmoDataFid') {
      return this._data?.fid;
    } else if (type === 'uidToFid' && uid) {
      return this._uidToFid[uid];
    } else if (type === 'gizmo' && (uid || fid)) {
      const gizmos = findGizmos({
        gizmo: this,
        uid,
        fid,
      });

      if (gizmos.length > 0) {
        return gizmos[0];
      }
    } else if (type === 'param' && path && this._params) {
      return gnov(this._params, path);
    } else if (type === 'rootGizmoDataStatus' && this._data) {
      if (this._data.status) {
        return this._data.status;
      } else {
        // 's0' means the root gizmo dasta is being created (new)
        return 's0';
      }
    } else if (type === 'catalogOnline' && catalogFid && fid) {
      // We're going to add the catalog request to the queue
      this._onlineCatalogRequests.push({
        gizmoFid: fid,
        catalogFid,
        parentCatalogFid,
        parentOptionFid: data,
      });

      // And notifiy the BehaviorSubject that there's a new request, so after the debounce time
      // we can process the requests
      this._onlineCatalogRequestsSubject?.next({ catalogFid });
    } else if (type === 'rootGizmoDataCreate') {
      return this._rootGizmoDataCreate;
    }

    return undefined;
  };

  /**
   * Callback used by the child Gizmos in order to set their data on the RootGizmoData
   *
   * @param {IGizmoData} data gizmo's data object that's going to be set on the root gizmo data
   * @param {string} fid Flowy id of the gizmo that's setting the data
   */
  private _setGizmoDataCB: TSetGizmoDataCB = ({ data, fid }): Promise<void> => {
    return new Promise<void>(async resolve => {
      if (this._data?.d) {
        if (data !== undefined) {
          this._data.d[fid] = data;
        } else {
          delete this._data.d[fid];
        }
      }

      const gizmoDataSubscriptions = this._subscriptions.filter(
        s => s.type === 'gizmoData'
      );
      for (const subscription of gizmoDataSubscriptions) {
        if (subscription.uid && this._uidToFid[subscription.uid] === fid) {
          const event: IGizmoEvent = {
            type: 'gizmo.data',
            src: {
              type: 'gizmo',
              gizmoFid: fid,
              gizmoUid: subscription.uid,
            },
          };
          this._events.next(event);
        } else if (subscription.fid && subscription.fid === fid) {
          const event: IGizmoEvent = {
            type: 'gizmo.data',
            src: {
              type: 'gizmo',
              gizmoFid: fid,
            },
          };

          this._events.next(event);
        }
      }

      resolve();
    });
  };

  /**
   * Callback function where the events sent by the Gizmos in the tree will arrive
   *
   * @param {IGizmoEvent} event event being received
   * @returns
   */
  private _sendEventCB: TSendEventCB = ({ event }): Promise<void> => {
    return new Promise<void>(async resolve => {
      await handleEvent({
        event,
        context: {
          callbacks: this._callbacks,
          rootGizmo: this,
          data: this._data,
          jsonData: this._jsonData,
          events: this._events,
          parentRootGizmoFid: this._parentRootGizmoFid,
          platformCallbacks: this._platformCallbacks,
          foundation: this._foundation,
          sectionStateMachine: this._sectionStateMachine,
        },
      });
      resolve();
    });
  };

  /**
   * Callback used by Gizmo or Lgx instances in order to communicate their subscriptions to the
   * RootGizmo, with this info the RootGizmo will emit events only when necessary
   *
   * @param {ISubscription[]} subscriptions list of subscriptions
   */
  private _setSubscriptionsCB: TSetSubscriptionsCB = ({
    subscriptions,
  }): void => {
    for (const subscription of subscriptions) {
      const { type, uid, fid } = subscription;
      const existingSubscription = this._subscriptions.find(s => {
        let present = false;
        if (s.type === type) {
          if (uid && s.uid === uid) {
            present = true;
          }
          if (fid && s.fid === fid) {
            present = true;
          }
        }

        return present;
      });

      // If the subscription doesn't exists we add it
      if (!existingSubscription) {
        this._subscriptions.push(subscription);
      }
    }
  };
}

interface IFindGizmos {
  type?: TGizmoType;
  fid?: string;
  uid?: string;
}

interface IFindGizmo {
  fid?: string;
  uid?: string;
}

export default RootGizmo;
